import type { WorldChunkAddress, WorldObjectOverride } from "@endclock/shared";

export interface WorldOverrideCacheSnapshot {
  readonly count: number;
  readonly chunkCount: number;
  readonly highestRevision: number;
}

/** Sparse, revision-ordered client presentation state for generated objects. */
export class ClientWorldOverrideCache {
  private readonly values = new Map<string, WorldObjectOverride>();
  private readonly objectIdsByChunk = new Map<string, Set<string>>();
  private highestRevision = 0;

  get size(): number {
    return this.values.size;
  }

  get(objectId: string): WorldObjectOverride | undefined {
    return this.values.get(objectId);
  }

  apply(override: WorldObjectOverride): boolean {
    if (!isValidOverride(override)) return false;
    const previous = this.values.get(override.objectId);
    if (previous && previous.revision >= override.revision) return false;
    if (previous && chunkKey(previous.ownerChunk) !== chunkKey(override.ownerChunk)) {
      this.removeFromChunk(previous.objectId, previous.ownerChunk);
    }
    const stored = freezeOverride(override);
    this.values.set(stored.objectId, stored);
    const key = chunkKey(stored.ownerChunk);
    const ids = this.objectIdsByChunk.get(key);
    if (ids) ids.add(stored.objectId);
    else this.objectIdsByChunk.set(key, new Set([stored.objectId]));
    this.highestRevision = Math.max(this.highestRevision, stored.revision);
    return true;
  }

  applyMany(overrides: readonly WorldObjectOverride[]): readonly string[] {
    const changed: string[] = [];
    for (const override of overrides) if (this.apply(override)) changed.push(override.objectId);
    return changed;
  }

  /**
   * Replaces one authoritative chunk hydration. WebSocket ordering ensures that
   * a later delta cannot be overtaken by an older sync within one connection.
   */
  hydrateChunk(ownerChunk: WorldChunkAddress, overrides: readonly WorldObjectOverride[]): readonly string[] {
    const key = chunkKey(ownerChunk);
    const nextIds = new Set(overrides.filter((override) => (
      chunkKey(override.ownerChunk) === key && isValidOverride(override)
    )).map((override) => override.objectId));
    const changed: string[] = [];
    const previousIds = this.objectIdsByChunk.get(key);
    if (previousIds) {
      for (const objectId of [...previousIds]) {
        if (nextIds.has(objectId)) continue;
        this.values.delete(objectId);
        previousIds.delete(objectId);
        changed.push(objectId);
      }
      if (previousIds.size === 0) this.objectIdsByChunk.delete(key);
    }
    for (const override of overrides) {
      if (chunkKey(override.ownerChunk) === key && this.apply(override)) changed.push(override.objectId);
    }
    if (changed.length > 0) this.recomputeHighestRevision();
    return changed;
  }

  hydrate(
    ownerChunks: readonly WorldChunkAddress[],
    overrides: readonly WorldObjectOverride[],
  ): readonly string[] {
    const byChunk = new Map<string, WorldObjectOverride[]>();
    for (const override of overrides) {
      const key = chunkKey(override.ownerChunk);
      const values = byChunk.get(key);
      if (values) values.push(override);
      else byChunk.set(key, [override]);
    }
    const changed: string[] = [];
    for (const ownerChunk of ownerChunks) {
      changed.push(...this.hydrateChunk(ownerChunk, byChunk.get(chunkKey(ownerChunk)) ?? []));
    }
    return changed;
  }

  clear(): boolean {
    if (this.values.size === 0) return false;
    this.values.clear();
    this.objectIdsByChunk.clear();
    this.highestRevision = 0;
    return true;
  }

  snapshot(): WorldOverrideCacheSnapshot {
    return {
      count: this.values.size,
      chunkCount: this.objectIdsByChunk.size,
      highestRevision: this.highestRevision,
    };
  }

  private removeFromChunk(objectId: string, ownerChunk: WorldChunkAddress): void {
    const key = chunkKey(ownerChunk);
    const ids = this.objectIdsByChunk.get(key);
    if (!ids) return;
    ids.delete(objectId);
    if (ids.size === 0) this.objectIdsByChunk.delete(key);
  }

  private recomputeHighestRevision(): void {
    let highestRevision = 0;
    for (const override of this.values.values()) highestRevision = Math.max(highestRevision, override.revision);
    this.highestRevision = highestRevision;
  }
}

function chunkKey(chunk: WorldChunkAddress): string {
  return `${chunk[0]}:${chunk[1]}`;
}

function isValidOverride(override: WorldObjectOverride): boolean {
  return override.objectId.length > 0
    && Number.isInteger(override.revision)
    && override.revision > 0
    && Number.isInteger(override.ownerChunk[0])
    && Number.isInteger(override.ownerChunk[1]);
}

function freezeOverride(override: WorldObjectOverride): WorldObjectOverride {
  return Object.freeze({
    objectId: override.objectId,
    state: override.state,
    revision: override.revision,
    ownerChunk: Object.freeze([override.ownerChunk[0], override.ownerChunk[1]] as const),
  });
}
